/**
 * useFirstTimeUser Hook
 * Detects if user is visiting for the first time and needs onboarding
 * P1-5: First-Time User Detection
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../lib/supabase';

interface FirstTimeUserState {
  isFirstTime: boolean;
  isLoading: boolean;
  needsOnboarding: boolean;
  focusArea: 'LIFE' | 'MONEY' | 'JOY' | 'balanced' | null;
}

export const useFirstTimeUser = (): FirstTimeUserState => {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<FirstTimeUserState>({
    isFirstTime: false,
    isLoading: true,
    needsOnboarding: false,
    focusArea: null,
  });

  useEffect(() => {
    const checkFirstTimeUser = async () => {
      if (authLoading) {
        return; // Wait for auth to load
      }

      if (!user) {
        setState({
          isFirstTime: false,
          isLoading: false,
          needsOnboarding: false,
          focusArea: null,
        });
        return;
      }

      try {
        // Check localStorage first (fast)
        const localOnboardingCompleted = localStorage.getItem('onboarding_completed');
        const localFocusArea = localStorage.getItem('onboarding_focus') as any;

        if (localOnboardingCompleted === 'true') {
          // Already completed onboarding
          setState({
            isFirstTime: false,
            isLoading: false,
            needsOnboarding: false,
            focusArea: localFocusArea || 'balanced',
          });
          return;
        }

        // Check Supabase profile (authoritative source)
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('onboarding_completed, focus_area, created_at')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking onboarding status:', error);

          // Default to showing onboarding if we can't verify
          setState({
            isFirstTime: true,
            isLoading: false,
            needsOnboarding: true,
            focusArea: null,
          });
          return;
        }

        // Determine if this is truly a first-time user
        const isFirstTime = !profile?.onboarding_completed;
        const needsOnboarding = isFirstTime;

        // Check if account is very new (created in last 5 minutes)
        const accountAge = profile?.created_at
          ? Date.now() - new Date(profile.created_at).getTime()
          : Infinity;
        const isVeryNew = accountAge < 5 * 60 * 1000; // 5 minutes

        // Sync to localStorage
        if (profile?.onboarding_completed) {
          localStorage.setItem('onboarding_completed', 'true');
          if (profile.focus_area) {
            localStorage.setItem('onboarding_focus', profile.focus_area);
          }
        }

        setState({
          isFirstTime: isFirstTime && isVeryNew,
          isLoading: false,
          needsOnboarding,
          focusArea: profile?.focus_area || null,
        });
      } catch (error) {
        console.error('Error in useFirstTimeUser:', error);
        setState({
          isFirstTime: false,
          isLoading: false,
          needsOnboarding: false,
          focusArea: null,
        });
      }
    };

    checkFirstTimeUser();
  }, [user, authLoading]);

  return state;
};

/**
 * Utility function to mark onboarding as complete
 */
export const completeOnboarding = async (userId: string, focusArea: string) => {
  try {
    // Update Supabase
    const { error } = await supabase
      .from('profiles')
      .update({
        onboarding_completed: true,
        focus_area: focusArea,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) {
      console.error('Error completing onboarding:', error);
      return false;
    }

    // Update localStorage
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_focus', focusArea);

    return true;
  } catch (error) {
    console.error('Error in completeOnboarding:', error);
    return false;
  }
};

/**
 * Utility function to reset onboarding (for testing)
 */
export const resetOnboarding = async (userId: string) => {
  try {
    // Update Supabase
    const { error } = await supabase
      .from('profiles')
      .update({
        onboarding_completed: false,
        focus_area: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) {
      console.error('Error resetting onboarding:', error);
      return false;
    }

    // Clear localStorage
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('onboarding_focus');

    return true;
  } catch (error) {
    console.error('Error in resetOnboarding:', error);
    return false;
  }
};
